import { handleActions } from "redux-actions";
import { fromJS, List, Map } from "immutable";
import { arrayMove } from "react-sortable-hoc";
import {
    MUTATE_LIST_CREATE,
    MUTATE_LIST_INSERT,
    MUTATE_LIST_MOVE,
    MUTATE_MAP_CREATE,
    MUTATE_MAP_REMOVE,
    MUTATE_MAP_SET,
    MUTATE_MIXED_CREATE,
    MUTATE_OPERATION_ENHANCED,
    MUTATE_UPDATE_SNAPSHOT_FROM_COMMITTED,
    MUTATE_CLEAR,
    MUTATE_OPERATIONS_SENT,
    MUTATE_RETRY_SYNC,
    MUTATE_UNCOMMITTED_MUTATIONS_SET,
} from "constants/action-types";
import { IRetrySyncAction, IUncommittedMutationsSet } from "actions/mutate";

type ListOrMap = Map<string, any> | List<any>;

interface IState {
    snapshots: Map<string, ListOrMap>;
    committedSnapshots: Map<string, ListOrMap>; // the server's snapshot
    HEADs: Map<string, any>;
    committedHEADs: Map<string, any>;
    uncommittedMutations: Map<string, List<any>>;
    sentMutations: Map<string, any>;
    mutationsLastSentAt: Map<string, number>;
    // Keeping track of initial uncommitted mutation count. Useful when we need the original count of pending mutations.
    startingMutationCount: Map<string, number>;
}

interface IPayload {
    [key: string]: any;
}

const initialState: IState = {
    HEADs: Map<string, any>(),
    committedHEADs: Map<string, any>(),
    snapshots: Map<string, ListOrMap>(),
    committedSnapshots: Map<string, ListOrMap>(),
    uncommittedMutations: Map<string, List<any>>(),
    sentMutations: Map<string, any>(),
    mutationsLastSentAt: Map<string, number>(),
    startingMutationCount: Map<string, number>(),
};

export const mutate = handleActions<IState, IPayload>(
    {
        [MUTATE_OPERATION_ENHANCED]: (state, { payload, meta }: any) => {
            const { snapshots, HEADs, uncommittedMutations } = state;
            const { objectId, HEAD, snapshot, requestId } = payload;
            const requestList = uncommittedMutations.get(objectId);

            const mutatedState = {
                ...state,
                HEADs: HEADs.set(objectId, HEAD),
                snapshots: snapshots.set(objectId, snapshot),
                uncommittedMutations: requestId
                    ? uncommittedMutations.set(objectId, requestList.push(payload))
                    : uncommittedMutations,
            };

            if (meta.batchSize > mutatedState.startingMutationCount.get(objectId, 0)) {
                mutatedState.startingMutationCount = mutatedState.startingMutationCount.set(objectId, meta.batchSize);
            }

            return mutatedState;
        },
        [MUTATE_OPERATIONS_SENT]: (state, { payload: { requestId, objectId, revision, sentMutationsIndex } }: any) => {
            const { sentMutations, uncommittedMutations, mutationsLastSentAt } = state;

            return {
                ...state,
                sentMutations: sentMutations.set(objectId, {
                    requestId,
                    revision,
                    mutations: uncommittedMutations.get(objectId).filter((existing, index) => {
                        // `sentMutationsIndex` is index of mutations in `uncommittedMutations` we want to send.
                        // This allows sending mutations in batches. `undefined` means send all.
                        return sentMutationsIndex === undefined || index === sentMutationsIndex;
                    }),
                }),
                mutationsLastSentAt: mutationsLastSentAt.set(objectId, Date.now()),
                uncommittedMutations: uncommittedMutations.updateIn([objectId], (existingUncommittedMutations) => {
                    // Filter-out sent mutations, leaving unsent mutations in `uncommittedMutations`.
                    // `undefined` means all mutations are sent.
                    return existingUncommittedMutations.filter(
                        (existing, index) => sentMutationsIndex !== undefined && index !== sentMutationsIndex
                    );
                }),
            };
        },
        [MUTATE_UPDATE_SNAPSHOT_FROM_COMMITTED]: (state, { payload }: any): IState => {
            const { snapshots, committedSnapshots, HEADs, committedHEADs, uncommittedMutations, mutationsLastSentAt } =
                state;
            const {
                objectId,
                committedHEADRevision,
                revision,
                snapshot,
                uncommittedMutationsForEntity,
                committedSnapshot,
                hasMyChanges = false,
            } = payload;
            const HEAD = HEADs.get(objectId);
            const committedHEAD = committedHEADs.get(objectId);
            const mutatedState = {
                ...state,
                HEADs: HEADs.set(objectId, { ...HEAD, revision }),
                committedHEADs: committedHEADs.set(objectId, { ...committedHEAD, revision: committedHEADRevision }),
                snapshots: snapshots.set(objectId, snapshot),
                uncommittedMutations: uncommittedMutations.set(objectId, uncommittedMutationsForEntity),
                committedSnapshots: committedSnapshots.set(objectId, committedSnapshot),
                sentMutations: state.sentMutations.delete(objectId),
                mutationsLastSentAt: hasMyChanges ? mutationsLastSentAt.delete(objectId) : mutationsLastSentAt,
            };

            // Clear starting mutation count when all uncommitted mutations are successfully sent
            if (uncommittedMutationsForEntity.size === 0) {
                mutatedState.startingMutationCount = mutatedState.startingMutationCount.delete(objectId);
            }

            return mutatedState;
        },
        [MUTATE_MAP_CREATE]: (state, { payload: { id, value }, meta: { ref, objectType } }: any) => {
            const { snapshots, uncommittedMutations, HEADs, committedHEADs } = state;
            const entity = Map.isMap(value) ? value : fromJS(value);
            return {
                ...state,
                HEADs: HEADs.set(id, { ...ref, object_type: objectType }),
                committedHEADs: committedHEADs.set(id, { ...ref, object_type: objectType }),
                snapshots: snapshots.set(id, entity),
                committedSnapshots: snapshots.set(id, entity),
                uncommittedMutations: uncommittedMutations.set(id, List<string>()),
            };
        },
        [MUTATE_MAP_SET]: (state, { payload: { id, key, value }, meta: { ref } }: any) => {
            const { snapshots, HEADs } = state;
            let entity = snapshots.get(id) as Map<string, any>;
            entity = entity.set(key, value);
            return {
                ...state,
                HEADs: HEADs.set(id, ref),
                snapshots: snapshots.set(id, entity),
            };
        },
        [MUTATE_MAP_REMOVE]: (state, { payload: { id, key } }) => {
            const { snapshots } = state;
            let entity = snapshots.get(id) as Map<string, any>; // create if now found
            entity = entity.remove(key);
            return {
                ...state,
                snapshots: snapshots.set(id, entity),
            };
        },
        [MUTATE_LIST_CREATE]: (state, { payload: { id, value }, meta: { ref, objectType } }: any) => {
            const { snapshots, HEADs, committedHEADs, uncommittedMutations } = state;
            const values = Array.isArray(value) ? value : [value];
            const entity = fromJS(values);
            return {
                ...state,
                HEADs: HEADs.set(id, { ...ref, object_type: objectType }),
                committedHEADs: committedHEADs.set(id, { ...ref, object_type: objectType }),
                snapshots: snapshots.set(id, entity),
                committedSnapshots: snapshots.set(id, entity),
                uncommittedMutations: uncommittedMutations.set(id, List<string>()),
            };
        },
        [MUTATE_LIST_INSERT]: (state, { payload: { id, index, value } }) => {
            const { snapshots } = state;
            let entity = snapshots.get(id) as List<any>;
            const values = Array.isArray(value) ? value : [value];
            if (index !== null && index !== undefined) {
                values.forEach((v, i) => (entity = entity.insert(index + i, v)));
            } else {
                values.forEach((v) => (entity = entity.push(v)));
            }
            return {
                ...state,
                snapshots: snapshots.set(id, entity),
            };
        },
        [MUTATE_LIST_MOVE]: (state, { payload: { id, oldIndex, newIndex } }) => {
            const { snapshots } = state;
            let entity = snapshots.get(id) as List<any>;
            entity = List(arrayMove(entity.toJS(), oldIndex, newIndex));
            return {
                ...state,
                snapshots: snapshots.set(id, entity),
            };
        },
        [MUTATE_MIXED_CREATE]: (state, { payload: { id, value }, meta: { ref, objectType } }: any) => {
            const { snapshots, uncommittedMutations, HEADs, committedHEADs } = state;
            const entity = Map.isMap(value) ? value : fromJS(value);
            return {
                ...state,
                HEADs: HEADs.set(id, { ...ref, object_type: objectType }),
                committedHEADs: committedHEADs.set(id, { ...ref, object_type: objectType }),
                snapshots: snapshots.set(id, entity),
                committedSnapshots: snapshots.set(id, entity),
                uncommittedMutations: uncommittedMutations.set(id, List<string>()),
            };
        },
        [MUTATE_CLEAR]: () => {
            return initialState;
        },
        [MUTATE_RETRY_SYNC]: (state, action: IRetrySyncAction): IState => {
            const { objectId } = action.payload;
            // On retry, take object's sentMutations and unshift to uncommittedMutations
            const sentMutation = state.sentMutations.get(objectId);
            const uncommittedMutations = (sentMutation ? sentMutation.mutations : List()).concat(
                state.uncommittedMutations.get(objectId, List())
            );
            return {
                ...state,
                uncommittedMutations: state.uncommittedMutations.set(objectId, uncommittedMutations),
                sentMutations: state.sentMutations.delete(objectId),
            };
        },
        [MUTATE_UNCOMMITTED_MUTATIONS_SET]: (state, action: IUncommittedMutationsSet): IState => {
            const { objectId, mutations } = action.payload;
            return {
                ...state,
                uncommittedMutations: state.uncommittedMutations.set(objectId, mutations),
            };
        },
    },
    initialState
);
